/**
 * PDF 导出的**浏览器适配层** —— 只有本文件碰 jsPDF / svg2pdf.js，且一律**动态 import**。
 *
 *  · 动态 import：两个库合计约 400KB，只在用户真的选 PDF 时才加载，首屏零负担。
 *  · 为什么与 core/io/pdf.ts 分开：svg2pdf.js 官宣**不支持 JSDOM**，它必须待在单测之外；
 *    页面盒 / 文件名 / 中文判定那些纯逻辑留在 pdf.ts（12 个用例兜着）。
 *  · 中文字体：jsPDF 内置只有 14 个西文标准字体，中文必须 addFont，否则丢字。字体用仓库里的
 *    子集 TTF（生成方式见 scripts/build-cjk-subset.py），**只在文档真的含非 ASCII 时才加载**，
 *    纯西文文档一分钱不花。
 */
import fontUrl from '@/assets/fonts/NotoSansSC-Regular-subset.ttf?url';
import type { jsPDF } from 'jspdf';
import { hasNonAscii, renderPdf, type PdfDeps, type PdfPage, type PdfResult } from './pdf';

/** 注册进 jsPDF 的字体名；含中文的文本节点会被改写成它 */
const FONT_FAMILY = 'NotoSansSC';
const FONT_VFS = 'NotoSansSC-Regular-subset.ttf';

export interface PdfFileRequest {
  svgText: string;
  /** 图幅（用户单位 = viewBox 尺寸） */
  width: number;
  height: number;
  page?: PdfPage;
  sourceName?: string | null;
}

/** ArrayBuffer → base64。字体 1.15MB，一次 apply 会爆参数上限，按块拼 */
function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x2000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x2000));
  }
  return btoa(binary);
}

/**
 * 离屏挂载一份 SVG 供转换（**绝不碰文档里那棵树**）。
 *
 * svg2pdf 要读 getComputedStyle / getBBox，所以元素必须在文档里 —— 但不能可见。
 * 含中文的文本节点改写成内嵌字体：这份字体同时含拉丁字形，混排节点整体改过去不会掉字；
 * 纯西文节点保持原样，由 jsPDF 回落到 Helvetica。
 */
function mountOffscreen(svgText: string, cjk: boolean): { element: SVGSVGElement; dispose: () => void } {
  const holder = document.createElement('div');
  holder.style.cssText = 'position:fixed;left:-100000px;top:0;width:0;height:0;overflow:hidden';
  holder.setAttribute('aria-hidden', 'true');
  document.body.appendChild(holder);
  holder.innerHTML = svgText;

  const element = holder.querySelector('svg');
  if (!element) {
    holder.remove();
    throw new Error('这段内容不是 SVG，无法导出 PDF');
  }

  if (cjk) {
    for (const node of Array.from(element.querySelectorAll('text, tspan'))) {
      if (hasNonAscii(node.textContent ?? '')) (node as SVGElement).style.fontFamily = FONT_FAMILY;
    }
  }
  return { element, dispose: () => holder.remove() };
}

/**
 * 导出 PDF（加载库 → 按需换字体 → 离屏转换 → 出字节）。
 * 返回 Blob 而不是自己落盘：调用方统一走 downloadFile，将来宿主想要字节也能直接拿。
 */
export async function buildPdf(req: PdfFileRequest): Promise<PdfResult> {
  const [{ jsPDF: JsPdf }, { svg2pdf }] = await Promise.all([import('jspdf'), import('svg2pdf.js')]);
  const cjk = hasNonAscii(req.svgText);

  const deps: PdfDeps = {
    createDoc(box) {
      // 页面盒已经是最终尺寸（朝向也在里面），**不再传 orientation** —— 否则会被 jsPDF 二次交换
      return new JsPdf({ unit: 'pt', format: [box.width, box.height], compress: true });
    },
    async draw(doc, svgText, box) {
      const { element, dispose } = mountOffscreen(svgText, cjk);
      try {
        await svg2pdf(element, doc as jsPDF, {
          x: box.x,
          y: box.y,
          width: box.contentWidth,
          height: box.contentHeight,
        });
      } finally {
        dispose();
      }
    },
    output(doc) {
      return (doc as jsPDF).output('blob');
    },
    async useFont(doc) {
      const res = await fetch(fontUrl);
      if (!res.ok) throw new Error(`中文字体加载失败（HTTP ${res.status}）`);
      const pdf = doc as jsPDF;
      pdf.addFileToVFS(FONT_VFS, toBase64(await res.arrayBuffer()));
      pdf.addFont(FONT_VFS, FONT_FAMILY, 'normal');
    },
  };

  return renderPdf(req, deps);
}
